import React, { useState, useCallback, useEffect } from 'react';
import { AuthContext, UserState } from './auth-context';
import { AuthUseCases } from '../../main/factories/auth-dependencies.factory';
import { LoginRequestDTO } from '../../application/dtos/login-request.dto';

export interface AuthProviderProps {
  children: React.ReactNode;
  authUseCases: AuthUseCases;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, authUseCases }) => {
  const [user, setUser] = useState<UserState | null>(() => {
    try {
      const session = authUseCases.getCurrentSessionUseCase.execute();
      return session ? session.user.toJSON() : null;
    } catch {
      return null;
    }
  });
  const [loading] = useState<boolean>(false);

  // Escuchar eventos de token expirado o 401 para limpiar la sesión y redirigir limpiamente
  useEffect(() => {
    const handleUnauthorized = () => {
      authUseCases.logoutUserUseCase.execute();
      setUser(null);
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard')) {
        window.location.href = '/login?expired=true';
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [authUseCases]);

  const login = useCallback(
    async (credentials: LoginRequestDTO): Promise<void> => {
      const session = await authUseCases.loginUserUseCase.execute(credentials);
      setUser(session.user.toJSON());
    },
    [authUseCases]
  );

  const logout = useCallback((): void => {
    authUseCases.logoutUserUseCase.execute();
    setUser(null);
  }, [authUseCases]);

  const hasPermission = useCallback(
    (code: string): boolean => {
      if (!user) return false;
      const roleNormalized = user.role?.toLowerCase();
      if (roleNormalized === 'admin' || roleNormalized === 'superadmin') return true;
      return user.permissions?.includes(code) ?? false;
    },
    [user]
  );

  const value = {
    isAuthenticated: !!user,
    user,
    role: user?.role ?? null,
    permissions: user?.permissions ?? [],
    hasPermission,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
